import React, { useCallback, useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
import { Box, Button, Chip, Typography } from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import { useApi } from '../context/ApiProvider';
import PageHeader from '../components/PageHeader';
import LoadingScreen from '../components/LoadingScreen';
import CandidatesTable from '../components/CandidatesTable';
import defaultArt from '../assets/images/dj_kip_art.png';

const STATUS_FILTERS = [
  { label: 'All', value: '' },
  { label: 'Candidate', value: 'candidate' },
  { label: 'Demo created', value: 'demo_created' },
];

function DemoCandidates() {
  const api = useApi();
  const { enqueueSnackbar } = useSnackbar();
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');

  const handleApiError = useCallback(
    (response, fallback) => {
      if (response.status === 403) {
        enqueueSnackbar('Admin access required', { variant: 'error' });
      } else {
        enqueueSnackbar(
          response.body?.description || response.body?.message || fallback,
          { variant: 'error' }
        );
      }
    },
    [enqueueSnackbar]
  );

  const loadCandidates = useCallback(async () => {
    setLoading(true);
    const query = statusFilter ? { status: statusFilter } : {};
    const response = await api.get('/demo/candidates', query);
    if (response.ok) {
      setCandidates(Array.isArray(response.body) ? response.body : []);
    } else {
      handleApiError(response, 'Failed to load demo candidates');
      setCandidates([]);
    }
    setLoading(false);
  }, [api, statusFilter, handleApiError]);

  useEffect(() => {
    loadCandidates();
  }, [loadCandidates]);

  const handleRefreshPool = async () => {
    setRefreshing(true);
    // No `since`: the API defaults to the start of the current year.
    const response = await api.post('/demo/artists/refresh', {});
    if (response.ok) {
      enqueueSnackbar('Candidate pool refreshed', { variant: 'success' });
      await loadCandidates();
    } else {
      handleApiError(response, 'Failed to refresh candidates');
    }
    setRefreshing(false);
  };

  return (
    <>
      {loading && <LoadingScreen />}
      <PageHeader backgroundArt={defaultArt} minHeight={350}>
        <Typography variant="h3" fontWeight="bold" mt={4}>
          Demo{' '}
          <Typography
            variant="h3"
            component="span"
            color="primary"
            fontWeight="bold"
          >
            Outreach
          </Typography>
        </Typography>
        <Typography mt={2}>
          Targeted artists for demo accounts and outreach tracking, ranked by
          how many of their tracks DJs played before release
        </Typography>
      </PageHeader>
      <Box sx={{ padding: { xs: '20px', lg: '30px 0' } }}>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1,
            alignItems: 'center',
            mb: 2,
          }}
        >
          {STATUS_FILTERS.map(({ label, value }) => (
            <Chip
              key={value || 'all'}
              label={label}
              color={statusFilter === value ? 'primary' : 'default'}
              onClick={() => setStatusFilter(value)}
              clickable
            />
          ))}
          <Box sx={{ flexGrow: 1 }} />
          <Button
            variant="contained"
            startIcon={<RefreshIcon />}
            onClick={handleRefreshPool}
            disabled={refreshing || loading}
          >
            Refresh from discovery
          </Button>
        </Box>

        {!loading && candidates.length === 0 && (
          <Typography color="text.secondary">
            No candidates yet. Run &quot;Refresh from discovery&quot; to
            populate the list.
          </Typography>
        )}

        <CandidatesTable items={candidates} />
      </Box>
    </>
  );
}

export default DemoCandidates;
